import { useState, useCallback, useId } from 'react'
import styles from './ScoopTab.module.scss'

/**
 * ScoopTab - A flexible, native HTML tab component with multiple variants
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{key: string|number, label: string, badgeCount?: number, disabled?: boolean}>} props.tabs - Array of tab definitions
 * @param {string|number} props.value - Currently selected tab key
 * @param {function} props.onChange - Callback when tab changes: (event, newValue) => void
 * @param {'pill' | 'underline' | 'minimal'} [props.variant='pill'] - Visual style variant
 *   - pill: Rounded container with background (default)
 *   - underline: Classic tabs with underline indicator
 *   - minimal: Simple text tabs without container
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Tab size
 * @param {boolean} [props.fullWidth=true] - Whether tabs should stretch to fill container
 * @param {boolean} [props.centered=false] - Whether to center the tabs
 * @param {boolean} [props.noPadding=false] - Whether to remove container padding
 * @param {string} [props.className] - Additional CSS classes for the container
 * @param {string} [props.tabClassName] - Additional CSS classes for individual tabs
 */
export const ScoopTab = ({
    tabs = [],
    value,
    onChange,
    variant = 'pill',
    size = 'md',
    fullWidth = true,
    centered = false,
    noPadding = false,
    className,
    tabClassName,
    ...props
}) => {
    const baseId = useId()

    const handleTabClick = useCallback(
        (event, tabKey) => {
            if (onChange) {
                onChange(event, tabKey)
            }
        },
        [onChange]
    )

    const handleKeyDown = useCallback(
        (event, currentIndex) => {
            const enabledTabs = tabs.filter((tab) => !tab.disabled)
            const currentEnabledIndex = enabledTabs.findIndex(
                (tab) => tab.key === tabs[currentIndex].key
            )

            let newIndex = -1

            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault()
                newIndex = (currentEnabledIndex + 1) % enabledTabs.length
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault()
                newIndex = (currentEnabledIndex - 1 + enabledTabs.length) % enabledTabs.length
            } else if (event.key === 'Home') {
                event.preventDefault()
                newIndex = 0
            } else if (event.key === 'End') {
                event.preventDefault()
                newIndex = enabledTabs.length - 1
            }

            if (newIndex !== -1) {
                const newTab = enabledTabs[newIndex]
                if (onChange) {
                    onChange(event, newTab.key)
                }
                // Focus the new tab
                const tabElement = document.getElementById(`${baseId}-tab-${newTab.key}`)
                tabElement?.focus()
            }
        },
        [tabs, onChange, baseId]
    )

    const containerClassName = [
        styles.container,
        styles[`variant-${variant}`],
        styles[`size-${size}`],
        fullWidth && styles.fullWidth,
        centered && styles.centered,
        noPadding && styles.noPadding,
        className,
    ]
        .filter(Boolean)
        .join(' ')

    const tabListClassName = [styles.tabList, styles[`variant-${variant}`]]
        .filter(Boolean)
        .join(' ')

    return (
        <div className={containerClassName} {...props}>
            <div className={tabListClassName} role="tablist" aria-orientation="horizontal">
                {tabs.map((tab, index) => {
                    const isSelected = tab.key === value
                    const tabId = `${baseId}-tab-${tab.key}`
                    const panelId = `${baseId}-panel-${tab.key}`

                    const tabClasses = [
                        styles.tab,
                        styles[`variant-${variant}`],
                        styles[`size-${size}`],
                        isSelected && styles.selected,
                        tab.disabled && styles.disabled,
                        fullWidth && styles.fullWidth,
                        tabClassName,
                    ]
                        .filter(Boolean)
                        .join(' ')

                    return (
                        <button
                            key={tab.key}
                            id={tabId}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            aria-controls={panelId}
                            tabIndex={isSelected ? 0 : -1}
                            disabled={tab.disabled}
                            className={tabClasses}
                            onClick={(e) => handleTabClick(e, tab.key)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                        >
                            <span className={styles.tabLabel}>{tab.label}</span>
                            {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                                <span className={styles.badge}>{tab.badgeCount}</span>
                            )}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

/**
 * ScoopTabPanel - Content panel for ScoopTab
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|number} props.value - The key of this panel (must match a tab key)
 * @param {string|number} props.selectedValue - Currently selected tab value
 * @param {React.ReactNode} props.children - Panel content
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.keepMounted=false] - Keep content mounted when not visible
 */
export const ScoopTabPanel = ({
    value,
    selectedValue,
    children,
    className,
    keepMounted = false,
    ...props
}) => {
    const isSelected = value === selectedValue

    if (!isSelected && !keepMounted) {
        return null
    }

    const panelClassName = [styles.tabPanel, !isSelected && styles.hidden, className]
        .filter(Boolean)
        .join(' ')

    return (
        <div
            role="tabpanel"
            hidden={!isSelected}
            id={`panel-${value}`}
            aria-labelledby={`tab-${value}`}
            className={panelClassName}
            {...props}
        >
            {children}
        </div>
    )
}

ScoopTab.displayName = 'ScoopTab'
ScoopTabPanel.displayName = 'ScoopTabPanel'
